import {
	Box,
	Heading,
	Input,
	Select,
	Stack,
	Tooltip,
	Text,
	Link,
	FormErrorMessage,
} from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import { useFormContext } from "react-hook-form";
import { IntegrationsSettingsMap } from "../../../types";
import FormTwoColumn from "./FormTwoColumn";

type Props = {
	data?: IntegrationsSettingsMap;
	errors?: any;
};

const Integrations: React.FC<Props> = (props: any) => {
	const { register } = useFormContext();
	const { errors } = props;

	return (
		<Box>
			<Heading fontWeight="semibold" fontSize="3xl" as="h2" mb="8">
				{__("Integrations", "magazine-blocks")}
			</Heading>
			<Stack
				bgColor="#FAFAFC"
				border="1px"
				borderColor="gray.200"
				borderRadius="lg"
				maxWidth="4xl"
				py="4"
				px="5"
			>
				<Stack gap="7">
					<Stack direction="column" alignItems="flex-start">
						<Heading
							as="h2"
							m="0"
							fontSize="xl"
							fontWeight="semibold"
						>
							{__("Date & Weather", "magazine-blocks")}
						</Heading>
						<Text mt="2">
							{__(
								"We use the Free OpenWeatherMap to get weather details. To get your API key, please "
							)}
							<Link
								href="https://docs.wpblockart.com/magazine-blocks/date-weather/#10-toc-title"
								color="blue.500"
								isExternal
							>
								{__(
									"check out this short guide",
									"magazine-blocks"
								)}
							</Link>
						</Text>
					</Stack>
					<FormTwoColumn
						label={__("Embed API Key", "magazine-blocks")}
						description={__(
							"Enter your OpenWeatherMap API key.",
							"magazine-blocks"
						)}
						labelAlignStart
					>
						<Stack>
							<Input
								{...register("integrations.dateWeatherApiKey", {
									value: props.data?.["dateWeatherApiKey"],
									required: __(
										"Api Key is required.",
										"magazine-blocks"
									),
								})}
								borderRadius="base"
							/>
							{errors?.integrations?.dateWeatherApiKey && (
								<p role="alert">
									{
										errors?.integrations?.dateWeatherApiKey
											.message
									}
								</p>
							)}
						</Stack>
					</FormTwoColumn>
				</Stack>
				<Stack gap="7">
					<FormTwoColumn
						label={__("Latitude", "magazine-blocks")}
						description={__(
							"Enter the latitude of your location (27.710192).",
							"magazine-blocks"
						)}
						labelAlignStart
					>
						<Stack>
							<Input
								{...register(
									"integrations.dateWeatherLatitude",
									{
										value: props.data?.[
											"dateWeatherLatitude"
										],
										required: __(
											"Latitude is required.",
											"magazine-blocks"
										),
										pattern: {
											value: /^-?(90(\.0+)?|[0-8]?\d(\.\d+)?)$/,
											message: __(
												"Please enter a valid latitude (-90 to 90, e.g., 27.710192)",
												"magazine-blocks"
											),
										},
									}
								)}
								borderRadius="base"
							/>
							{errors?.integrations?.dateWeatherLatitude && (
								<p role="alert">
									{
										errors?.integrations
											?.dateWeatherLatitude.message
									}
								</p>
							)}
						</Stack>
					</FormTwoColumn>
				</Stack>
				<Stack gap="7">
					<FormTwoColumn
						label={__("Longitude", "magazine-blocks")}
						description={__(
							"Enter the longitude of your location (85.324167).",
							"magazine-blocks"
						)}
						labelAlignStart
					>
						<Stack>
							<Input
								{...register(
									"integrations.dateWeatherLongitude",
									{
										value: props.data?.[
											"dateWeatherLongitude"
										],
										required: __(
											"Longitude is required.",
											"magazine-blocks"
										),
										pattern: {
											value: /^-?(180(\.0+)?|1[0-7]\d(\.\d+)?|[0-9]?\d(\.\d+)?)$/,
											message: __(
												"Please enter a valid longitude (-180 to 180, e.g., 85.324167)",
												"magazine-blocks"
											),
										},
									}
								)}
								borderRadius="base"
							/>
							{errors?.integrations?.dateWeatherLongitude && (
								<p role="alert">
									{
										errors?.integrations
											?.dateWeatherLongitude.message
									}
								</p>
							)}
						</Stack>
					</FormTwoColumn>
				</Stack>
				<Stack gap="7">
					<FormTwoColumn
						label={__("Unit", "magazine-blocks")}
						description={__(
							"Choose the unit for temperature. Options: Celsius (metric) or Fahrenheit (imperial).",
							"magazine-blocks"
						)}
						labelAlignStart
					>
						<Stack>
							<Select
								{...register("integrations.dateWeatherUnit", {
									value:
										props.data?.["dateWeatherUnit"] ||
										"imperial",
								})}
								bg="unset !important"
								borderColor="gray.200 !important"
								sx={{
									"&.chakra-select": {
										maxWidth: "full !important",
									},
								}}
							>
								<option value="imperial">
									{__("Fahrenheit", "magazine-blocks")}
								</option>
								<option value="metric">
									{__("Celsius", "magazine-blocks")}
								</option>
							</Select>
						</Stack>
					</FormTwoColumn>
				</Stack>
			</Stack>
		</Box>
	);
};

export default Integrations;
